import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../../utils/api';
import { useToast } from '../../../context/ToastContext';
import { usePortal } from '../../../context/PortalContext';
import styles from './DepartmentStats.module.css';

/**
 * DepartmentStats Tab
 * 
 * Displays all department stats in a large, scrollable table.
 * Features:
 * - Sticky first column (officer name)
 * - Rotated column headers (categories)
 * - Horizontal scroll for many categories
 * - Shift selector for data filtering
 * - Shift total rows and department total row
 */
function DepartmentStats() {
  const api = useApi();
  const { pushToast } = useToast();
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedShift, setSelectedShift] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dateRange, setDateRange] = useState('month');
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}-01`;
  });
  const [dateTo, setDateTo] = useState(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (range === 'month') {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      setDateFrom(`${yyyy}-${mm}-01`);
      setDateTo(`${yyyy}-${mm}-${dd}`);
    } else if (range === 'allTime') {
      setDateFrom('');
      setDateTo('');
    }
  };
  const { state: portalState } = usePortal();
  const shifts = useMemo(() => {
    const all = [{ id: '', label: 'All Shifts' }];
    (portalState.shifts || []).forEach((s) => all.push({ id: String(s.id), label: s.name }));
    return all;
  }, [portalState.shifts]);
  const categories = useMemo(() => {
    const all = [{ id: '', label: 'All Categories' }];
    (portalState.categories || []).forEach((c) => all.push({ id: String(c.id), label: c.name }));
    return all;
  }, [portalState.categories]);

  useEffect(() => {
    loadStats();
  }, [selectedShift, selectedCategory, dateFrom, dateTo]);

  const loadStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await api.getDepartmentStats({
        shiftId: selectedShift,
        categoryId: selectedCategory,
        dateFrom,
        dateTo,
      });
      setTableData(parseTableData(data));
    } catch (err) {
      const message = err.message || 'Failed to load department stats';
      setError(message);
      pushToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Department Stats</h2>

      {error && <div className={styles.alert + ' ' + styles.error} role="alert">{error}</div>}

      <div className={styles.filterBar}>
        <label className={styles.filterGroup}>
          <span className={styles.label}>Shift</span>
          <select
            id="shift"
            value={selectedShift}
            onChange={(e) => setSelectedShift(e.target.value)}
            className={styles.select}
          >
            {shifts.map((shift) => (
              <option key={shift.id} value={shift.id}>
                {shift.label}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.filterGroup}>
          <span className={styles.label}>Category</span>
          <select
            id="category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className={styles.select}
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.label}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.filterGroup}>
          <span className={styles.label}>Date Range</span>
          <select
            id="dateRange"
            value={dateRange}
            onChange={(e) => handleDateRangeChange(e.target.value)}
            className={styles.select}
          >
            <option value="month">This Month</option>
            <option value="allTime">All Time</option>
            <option value="custom">Custom</option>
          </select>
        </label>

        {dateRange === 'custom' && (
          <>
            <label className={styles.filterGroup}>
              <span className={styles.label}>From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={styles.select}
              />
            </label>

            <label className={styles.filterGroup}>
              <span className={styles.label}>To</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={styles.select}
              />
            </label>
          </>
        )}
      </div>

      {loading && <p className={styles.loading}>Loading...</p>}

      {!loading && tableData && tableData.rows.length === 0 && (
        <p className={styles.loading}>No stats for this period.</p>
      )}

      {!loading && tableData && tableData.rows.length > 0 && (
        <div
          className={styles.tableScrollWrapper}
          aria-busy={loading}
          style={{ '--stats-columns': tableData.headers.length }}
        >
          <table className={styles.table}>
            <caption className={styles.caption}>Department totals grouped by category and officer.</caption>
            <thead>
              <tr>
                <th className={styles.stickyCol} scope="col">Officer</th>
                {tableData.headers.map((header) => (
                  <th key={header.id} className={styles.rotatedHeader} scope="col">
                    <div className={styles.rotatedText}>{header.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, idx) => (
                <tr
                  key={idx}
                  className={row.isDepartmentTotal ? styles.departmentTotalRow : (row.isShiftTotal ? styles.shiftTotalRow : '')}
                >
                  <th className={styles.stickyCol} scope="row">{row.label}</th>
                  {row.values.map((val, vidx) => (
                    <td
                      key={vidx}
                      className={row.isDepartmentTotal ? styles.departmentTotalCell : (row.isShiftTotal ? styles.shiftTotalCell : '')}
                    >
                      {val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Parse JSON response from dst_get_department_stats_table into structured data.
 */
function parseTableData(data) {
  if (!data || !Array.isArray(data.headers) || !Array.isArray(data.rows)) {
    return null;
  }
  return {
    headers: data.headers,
    rows: data.rows,
  };
}

export default DepartmentStats;
